import { format, parseISO, addDays, addMonths, startOfMonth, startOfWeek } from 'date-fns';

/**
 * Zero-fills bucket gaps so a chart shows a bar for EVERY bucket in the visible
 * range, not just the ones that had data. Backend by-source RPCs return only
 * non-empty buckets, so a sparse series (e.g. 2 months of data over 2 years)
 * would otherwise render as a couple of fat bars smeared across the axis. This
 * builds a continuous spine from startDate..endDate at the display-bucket grain
 * and left-joins the rows onto it (matched by canonical period key: 'yyyy-MM'
 * for month, Sunday-start for week, the day itself for day). Existing rows win
 * — a bucket with data keeps all its fields; an empty bucket becomes
 * { date, fullDate, ...zeroFields }. Mirrors the generate_series spine that
 * get_topic_engagement_daily already does server-side, applied client-side so
 * it works for the by-source endpoints too. Call AFTER bucketing, BEFORE any
 * rolling-average pass so the average runs across the zeros as well.
 */
export function fillBucketGaps(rows, startDate, endDate, bucketPeriod, zeroFields) {
    if (!startDate || !endDate) return rows;
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return rows;

    const keyOf = (d) => {
        if (bucketPeriod === 'month') return format(d, 'yyyy-MM');
        if (bucketPeriod === 'week') return format(startOfWeek(d, { weekStartsOn: 0 }), 'yyyy-MM-dd');
        return format(d, 'yyyy-MM-dd');
    };
    const labelOf = (d) => (bucketPeriod === 'month' ? format(d, 'MMM yyyy') : format(d, 'MMM dd'));

    const byKey = new Map();
    (rows || []).forEach(r => { if (r && r.fullDate) byKey.set(keyOf(parseISO(r.fullDate)), r); });

    const step = (d) => (bucketPeriod === 'month' ? addMonths(d, 1)
        : bucketPeriod === 'week' ? addDays(d, 7)
            : addDays(d, 1));

    let cursor = bucketPeriod === 'month' ? startOfMonth(start)
        : bucketPeriod === 'week' ? startOfWeek(start, { weekStartsOn: 0 })
            : start;

    const spine = [];
    while (cursor <= end) {
        const existing = byKey.get(keyOf(cursor));
        spine.push(existing || { date: labelOf(cursor), fullDate: format(cursor, 'yyyy-MM-dd'), ...zeroFields });
        cursor = step(cursor);
    }
    return spine;
}

// Sums every `src_*` field across a group of rows, so week/month bucketing
// preserves an optional per-source breakdown (e.g. the "By Source" toggle)
// instead of silently dropping it. A no-op ({}) for rows without such keys.
function sumSourceFields(rows) {
    const sums = {};
    rows.forEach(row => {
        Object.keys(row).forEach(key => {
            if (key.startsWith('src_')) {
                sums[key] = (sums[key] || 0) + (row[key] || 0);
            }
        });
    });
    return sums;
}

/**
 * Groups an ascending-by-fullDate array of { date, fullDate, likeCount } rows
 * into day/week/month buckets, summing likeCount within each bucket. 'day'
 * passes rows through unchanged. Extracted verbatim (same grouping/summing
 * logic) from PostLikesAnalytics.js's former inline handleSearch.
 */
export function bucketRowsByPeriod(processedData, bucketPeriod) {
    if (bucketPeriod === 'week') {
        const weeklyData = [];
        let currentWeek = [];
        let currentWeekStart = null;

        processedData.forEach(item => {
            const itemDate = parseISO(item.fullDate);
            const weekStart = new Date(itemDate);
            weekStart.setDate(weekStart.getDate() - weekStart.getDay());

            if (!currentWeekStart || weekStart.getTime() !== currentWeekStart.getTime()) {
                if (currentWeek.length > 0) {
                    const totalLikes = currentWeek.reduce((sum, day) => sum + day.likeCount, 0);
                    weeklyData.push({
                        date: format(currentWeekStart, 'MMM dd'),
                        fullDate: format(currentWeekStart, 'yyyy-MM-dd'),
                        likeCount: totalLikes,
                        ...sumSourceFields(currentWeek)
                    });
                }
                currentWeek = [item];
                currentWeekStart = weekStart;
            } else {
                currentWeek.push(item);
            }
        });

        if (currentWeek.length > 0) {
            const totalLikes = currentWeek.reduce((sum, day) => sum + day.likeCount, 0);
            weeklyData.push({
                date: format(currentWeekStart, 'MMM dd'),
                fullDate: format(currentWeekStart, 'yyyy-MM-dd'),
                likeCount: totalLikes,
                ...sumSourceFields(currentWeek)
            });
        }

        return weeklyData;
    }

    if (bucketPeriod === 'month') {
        const monthlyData = [];
        let currentMonth = [];
        let currentMonthKey = null;

        processedData.forEach(item => {
            const itemDate = parseISO(item.fullDate);
            const monthKey = format(itemDate, 'yyyy-MM');

            if (currentMonthKey !== monthKey) {
                if (currentMonth.length > 0) {
                    const totalLikes = currentMonth.reduce((sum, day) => sum + day.likeCount, 0);
                    const firstDayOfMonth = parseISO(currentMonth[0].fullDate);
                    monthlyData.push({
                        date: format(firstDayOfMonth, 'MMM yyyy'),
                        fullDate: format(firstDayOfMonth, 'yyyy-MM-dd'),
                        likeCount: totalLikes,
                        ...sumSourceFields(currentMonth)
                    });
                }
                currentMonth = [item];
                currentMonthKey = monthKey;
            } else {
                currentMonth.push(item);
            }
        });

        if (currentMonth.length > 0) {
            const totalLikes = currentMonth.reduce((sum, day) => sum + day.likeCount, 0);
            const firstDayOfMonth = parseISO(currentMonth[0].fullDate);
            monthlyData.push({
                date: format(firstDayOfMonth, 'MMM yyyy'),
                fullDate: format(firstDayOfMonth, 'yyyy-MM-dd'),
                likeCount: totalLikes,
                ...sumSourceFields(currentMonth)
            });
        }

        return monthlyData;
    }

    return processedData;
}

/**
 * Adds a rollingAvg field (trailing window average of likeCount) to each
 * bucketed row. rollingDays is a day-count; for week/month buckets it's
 * converted to a bucket-count window (same conversion as the original
 * inline logic: /7 for week, /30 for month, rounded up).
 */
export function addRollingAverage(aggregatedData, rollingDays, bucketPeriod) {
    return aggregatedData.map((item, index) => {
        let windowSize = rollingDays;
        if (bucketPeriod === 'week') {
            windowSize = Math.ceil(rollingDays / 7);
        } else if (bucketPeriod === 'month') {
            windowSize = Math.ceil(rollingDays / 30);
        }

        const startIndex = Math.max(0, index - (windowSize - 1));
        const rollingWindow = aggregatedData.slice(startIndex, index + 1);

        const sum = rollingWindow.reduce((acc, dayData) => acc + dayData.likeCount, 0);
        const average = sum / rollingWindow.length;

        return {
            ...item,
            rollingAvg: Math.round(average * 10) / 10
        };
    });
}
